import { useMemo } from "react";
import { useTranslate } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import type { TTextilePurchase } from "@/api/customs/textiles";

interface KAMGroupedTableProps {
  data: TTextilePurchase[];
  totalCost: number | null;
}

interface KAMSummary {
  kam: string;
  revenueConIva: number;
  recaudoConIva: number;
  revenueSinIva: number;
  recaudoSinIva: number;
}

export const KAMGroupedTable = ({ data, totalCost }: KAMGroupedTableProps) => {
  const translate = useTranslate();
  const kamSummaries = useMemo(() => {
    const summaryMap: Record<string, KAMSummary> = {};

    data.forEach((purchase) => {
      const kam = purchase.kam;
      
      if (!summaryMap[kam]) {
        summaryMap[kam] = {
          kam,
          revenueConIva: 0,
          recaudoConIva: 0,
          revenueSinIva: 0,
          recaudoSinIva: 0,
        };
      }

      // Revenue suma todo
      summaryMap[kam].revenueConIva += purchase.revenue_con_iva;
      summaryMap[kam].revenueSinIva += purchase.revenue_sin_iva;

      // Recaudo suma cuando PAYMENT_METHOD = 'CREDIT' O PAYMENT_STATUS = 'PAID'
      if (purchase.payment_method === "CREDIT" || purchase.payment_status === "PAID") {
        summaryMap[kam].recaudoConIva += purchase.revenue_con_iva;
        summaryMap[kam].recaudoSinIva += purchase.revenue_sin_iva;
      }
    });

    return Object.values(summaryMap).sort((a, b) => 
      a.kam.localeCompare(b.kam)
    );
  }, [data]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const totals = useMemo(() => {
    return kamSummaries.reduce(
      (acc, kam) => ({
        revenueConIva: acc.revenueConIva + kam.revenueConIva,
        recaudoConIva: acc.recaudoConIva + kam.recaudoConIva,
        revenueSinIva: acc.revenueSinIva + kam.revenueSinIva,
        recaudoSinIva: acc.recaudoSinIva + kam.recaudoSinIva,
      }),
      {
        revenueConIva: 0,
        recaudoConIva: 0,
        revenueSinIva: 0,
        recaudoSinIva: 0,
      }
    );
  }, [kamSummaries]);

  return (
    <Box>
      {totalCost !== null && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {translate("resources.textiles.fields.costo_total")}
            </Typography>
            <Typography variant="h3" color="primary" fontWeight="bold">
              {formatCurrency(totalCost)}
            </Typography>
          </CardContent>
        </Card>
      )}
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle2" fontWeight="bold">
                KAM
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle2" fontWeight="bold">
                Revenue con IVA
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle2" fontWeight="bold">
                Recaudo con IVA
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle2" fontWeight="bold">
                Revenue sin IVA
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle2" fontWeight="bold">
                Recaudo sin IVA
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {kamSummaries.map((kam) => (
            <TableRow key={kam.kam}>
              <TableCell>{kam.kam}</TableCell>
              <TableCell align="right">
                {formatCurrency(kam.revenueConIva)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(kam.recaudoConIva)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(kam.revenueSinIva)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(kam.recaudoSinIva)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ backgroundColor: "action.hover" }}>
            <TableCell>
              <Typography variant="subtitle2" fontWeight="bold">
                TOTAL
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle2" fontWeight="bold">
                {formatCurrency(totals.revenueConIva)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle2" fontWeight="bold">
                {formatCurrency(totals.recaudoConIva)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle2" fontWeight="bold">
                {formatCurrency(totals.revenueSinIva)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle2" fontWeight="bold">
                {formatCurrency(totals.recaudoSinIva)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
};

